import { useEffect, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useFetchTrigger } from '../../lib/useFetchTrigger';

export interface BookingStats {
  total: number;
  byStatus: Record<string, number>;
}

// Pure: build the GET /api/bookings/stats query for the toolbar's chip counts.
// Keyed on the NON-status filters only (search + area) — the stats return a
// count per status over that context, so each chip shows its true total rather
// than the count within the loaded 20-row page. status/sort/pagination are
// deliberately omitted. Exported for unit testing.
export function buildBookingStatsQuery(q: string | null, area: string | null): string {
  const p = new URLSearchParams();
  const term = q?.trim();
  if (term) p.set('q', term);
  if (area) p.set('area', area);
  const qs = p.toString();
  return qs ? `/bookings/stats?${qs}` : '/bookings/stats';
}

// Pick the toolbar chip counts: prefer the server per-status stats whenever they
// have loaded, in EVERY view (table, calendar, charts). A status is a global
// snapshot — a dog is "arrived" or not, regardless of the month on screen — so
// month-scoping the calendar chips made "Arrived" read differently there than in
// the table (the reported "arrived count wrong"). The row-derived counts/total
// are only a pre-load fallback. Pure → unit-tested.
export function resolveChipCounts(
  stats: BookingStats | null,
  fallbackCounts: Record<string, number>,
  fallbackTotal: number,
): { counts: Record<string, number>; total: number } {
  return stats ? { counts: stats.byStatus, total: stats.total } : { counts: fallbackCounts, total: fallbackTotal };
}

// The toolbar status-chip counts — per-status totals + grand total over ALL
// bookings matching the current search + area, independent of the active status
// chip and of pagination (the bug this fixes: a chip read 0 until selected,
// because counts came from the loaded page). Mirrors the owners stat strip:
// revalidates silently on a return to the page (keep-alive activation tick) and
// on a reload() after a same-page write, holding the current numbers across the
// fetch so they never flash to zero.
export function useBookingStats(q: string | null, area: string | null, enabled = true): {
  stats: BookingStats | null;
  reload: () => void;
} {
  const [stats, setStats] = useState<BookingStats | null>(null);
  // The shared SWR trigger core (lib/useFetchTrigger): reload() and the
  // activation+mutation tick both fire a cache-bypassing refetch; the counts on
  // screen hold across it (no flash to zero).
  const trigger = useFetchTrigger();

  useEffect(() => {
    if (!enabled) return; // the Today page only needs the live count on the real today
    const run = trigger.begin();
    api<BookingStats>(buildBookingStatsQuery(q, area), run.init)
      .then((body) => {
        if (!run.isCurrent()) return;
        run.markContent();
        if (body.data) setStats(body.data);
      })
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 401) return; // already redirected
        // A failed silent revalidation holds the last-known counts (no flash).
      });
    return run.cancel;
  }, [q, area, trigger.nonce, trigger.tick, enabled]);

  return { stats, reload: trigger.reload };
}
